import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, FormControl } from '@primer/react';

import { useCommonContext } from '../../../contexts/common';
import { useCommonSelectedHook } from '../../../hooks/useCommonSelectedHook';
import { ts_hocsinh_lichsu_yte_khothuoc } from '../../../models/response/category/ts_hocsinh_lichsu_yte_khothuoc';
import { khoThuocApi } from '../../../api/khoThuocApi';
import { NotifyHelper } from '../../../helpers/toast';
import Modal from '../../../components/ui/modal';
import TextInput from '../../../components/ui/text-input';
import Text from '../../../components/ui/text';
import ComboboxLoaiThuoc from '../../../components/combobox-loai-thuoc/ComboboxLoaiThuoc';

interface KhoThuocNhapModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveSuccess: () => void;
}

const KhoThuocNhapModal: React.FC<KhoThuocNhapModalProps> = ({ isOpen, onClose, onSaveSuccess }) => {
    const { translate } = useCommonContext();
    const { dm_coso_id } = useCommonSelectedHook();
    const [isLoading, setIsLoading] = useState(false);

    const { handleSubmit, formState: { errors, isValid }, watch, setValue, reset, control, clearErrors, trigger } = useForm<ts_hocsinh_lichsu_yte_khothuoc>({
        mode: "onChange", // Validate khi người dùng thay đổi input
        reValidateMode: "onChange", // Re-validate khi có thay đổi
        defaultValues: {
            id: 0,
            ts_hocsinh_lichsu_yte_loaithuoc_id: 0,
            loai_giao_dich: "NHAP",
            so_luong: 0,
            hsd: "",
            ghi_chu: "",
            dm_coso_id: dm_coso_id,
        }
    });

    const onSubmit = async (data: ts_hocsinh_lichsu_yte_khothuoc) => {
        try {
            setIsLoading(true);
            data.dm_coso_id = dm_coso_id;
            data.loai_giao_dich = "NHAP";

            const response = await khoThuocApi.insert(data);
            if (response.is_success) {
                NotifyHelper.Success(translate("Base.Message.SaveSuccess"));
                reset();
                onSaveSuccess();
                onClose();
            } else {
                NotifyHelper.Error(response.message || translate("Base.Message.SaveFailed"));
            }
        } catch (error) {
            console.error('Error saving:', error);
            NotifyHelper.Error(translate("Base.Message.SaveFailed"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Nhập thuốc vào kho"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 0}}>
                    <FormControl>
                        <FormControl.Label>
                            <Text text="Loại thuốc" />
                        </FormControl.Label>
                        <Controller
                            name="ts_hocsinh_lichsu_yte_loaithuoc_id"
                            control={control}
                            rules={{
                                required: "Vui lòng chọn loại thuốc",
                                validate: (value) => {
                                    if (!value || value === 0) {
                                        return "Vui lòng chọn loại thuốc";
                                    }
                                    return true;
                                }
                            }}
                            render={({ field }) => (
                                <ComboboxLoaiThuoc
                                    width={'100%'}
                                    value={field.value || 0}
                                    onValueChanged={(value) => {
                                        field.onChange(value);
                                        clearErrors("ts_hocsinh_lichsu_yte_loaithuoc_id");
                                    }}
                                    isShowClearButton={true}
                                />
                            )}
                        />
                        {errors.ts_hocsinh_lichsu_yte_loaithuoc_id && (
                            <FormControl.Validation variant="error">
                                {errors.ts_hocsinh_lichsu_yte_loaithuoc_id.message}
                            </FormControl.Validation>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormControl.Label>
                            <Text text="Số lượng" />
                        </FormControl.Label>
                        <Controller
                            name="so_luong"
                            control={control}
                            rules={{
                                required: "Vui lòng nhập số lượng",
                                min: {
                                    value: 0.01,
                                    message: "Số lượng phải lớn hơn 0"
                                },
                                validate: (value) => {
                                    if (!value || value <= 0) {
                                        return "Số lượng phải lớn hơn 0";
                                    }
                                    return true;
                                }
                            }}
                            render={({ field }) => (
                                <TextInput
                                    block
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(parseFloat(e.target.value) || 0);
                                        clearErrors("so_luong");
                                    }}
                                    placeholder="Nhập số lượng"
                                />
                            )}
                        />
                        {errors.so_luong && (
                            <FormControl.Validation variant="error">
                                {errors.so_luong.message}
                            </FormControl.Validation>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormControl.Label>
                            <Text text="Hạn sử dụng" />
                        </FormControl.Label>
                        <Controller
                            name="hsd"
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    block
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        clearErrors("hsd");
                                    }}
                                    placeholder="VD: 12/2025"
                                />
                            )}
                        />
                        {errors.hsd && (
                            <FormControl.Validation variant="error">
                                {errors.hsd.message}
                            </FormControl.Validation>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormControl.Label>
                            <Text text="Ghi chú" />
                        </FormControl.Label>
                        <Controller
                            name="ghi_chu"
                            control={control}
                            render={({ field }) => (
                                <textarea
                                    style={{
                                        width: '100%',
                                        minHeight: '80px',
                                        padding: '8px',
                                        border: '1px solid #d0d7de',
                                        borderRadius: '6px',
                                        resize: 'vertical',
                                        fontFamily: 'inherit',
                                        fontSize: '14px'
                                    }}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        clearErrors("ghi_chu");
                                    }}
                                    placeholder="Ghi chú về lô thuốc..."
                                />
                            )}
                        />
                        {errors.ghi_chu && (
                            <FormControl.Validation variant="error">
                                {errors.ghi_chu.message}
                            </FormControl.Validation>
                        )}
                    </FormControl>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
                        <Button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            {translate("Base.Label.Cancel")}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={isLoading}
                        >
                            {translate("Base.Label.Save")}
                        </Button>
                    </Box>
                </Box>
            </form>
        </Modal>
    );
};

export default KhoThuocNhapModal;
